// Content for the "ESG Professional: End-to-End Implementation" course,
// converted from its reading-lesson form (see server.js, the
// `esg-professional-3day` course entry, 16 modules across Day 1/2/3) into a
// live-style slide presentation, following the same pattern as
// civils2000ClaudeProfessionals.js, bbbeeMasterclass.js and
// esgFoundationsReporting.js. All substantive ESG/GRI/King IV/climate/ratings/
// assurance content below is condensed directly from that source course —
// nothing here introduces a new fact, figure or standard requirement beyond
// what the source already stated, and every place the source hedged on a
// current standard, threshold, methodology or requirement is hedged the same
// way here. No content from Harvard Case Studies or Harvard Business
// Publishing material is used or referenced anywhere in this deck.

import { applyOkiruTemplate } from '../okiruPresentationTemplate';

export const manifest = applyOkiruTemplate({
  id: 'esg-professional-3day',
  title: 'ESG Professional: End-to-End Implementation',
  subtitle: 'Okiru Learn',
  modules: [
    { index: 1, name: 'Owning ESG Implementation End to End' },
    { index: 2, name: 'Advanced Materiality — Facilitation and Scoring' },
    { index: 3, name: 'Environmental Data Architecture — Emissions and Energy at Scale' },
    { index: 4, name: 'Social Data Architecture — Workforce, Community and Human Rights' },
    { index: 5, name: 'Governance Architecture — King IV and Assurance-Ready Controls' },
    { index: 6, name: 'Building the Enterprise ESG Data-Owner RACI Matrix' },
    { index: 7, name: 'GRI-Aligned Report Structure and General Disclosures' },
    { index: 8, name: 'Worked Example — Drafting the Environmental Disclosures Section' },
    { index: 9, name: 'Worked Example — Drafting the Social and Governance Disclosures Sections' },
    { index: 10, name: 'JSE Guidance and Listed-Company Considerations' },
    { index: 11, name: 'Climate Risk Fundamentals — Physical Risk, Transition Risk and Scenarios' },
    { index: 12, name: 'How ESG Ratings Agencies Score Companies' },
    { index: 13, name: 'Assurance and Verification Readiness' },
    { index: 14, name: 'Investor and Lender ESG Due-Diligence Expectations' },
    { index: 15, name: 'Running a Supply-Chain ESG Audit' },
    { index: 16, name: "Capstone — Building Your Organisation's First Full ESG Report and 12-Month Improvement Plan" },
  ],
}, 'okiru-light');

export const slides = [
  // ---------------------------------------------------------------------
  // Front matter
  // ---------------------------------------------------------------------
  {
    id: 1, layout: 'title',
    kicker: 'Okiru Learn',
    title: 'ESG Professional: End-to-End Implementation',
    lead: 'A three-day advanced programme for the person who will personally own ESG implementation end to end: RACI-based data ownership, a full GRI-aligned report built from scratch, climate-risk and ratings-agency literacy, assurance readiness, investor due diligence and a supply-chain ESG audit.',
    meta: ['16 modules · 3 days', 'Advanced level', 'Okiru Learn'],
  },
  {
    id: 2, layout: 'statement',
    text: 'By the end of these three days, you can personally own ESG implementation end to end: a governed RACI matrix, a drafted GRI-aligned report, climate-risk and ratings-agency literacy, and an evidence trail ready for assurance, investors and a supply-chain audit.',
    footnote: 'Every artefact you build across these three days should be one you could hand to an external assurance provider without apology.',
  },
  {
    id: 3, layout: 'agenda',
    kicker: 'The journey', title: 'Sixteen modules across three days',
    columns: [
      [
        { time: 'D1', label: 'Module 1 — Owning ESG Implementation End to End' },
        { time: 'D1', label: 'Module 2 — Advanced Materiality: Facilitation and Scoring' },
        { time: 'D1', label: 'Module 3 — Environmental Data Architecture at Scale' },
        { time: 'D1', label: 'Module 4 — Social Data Architecture' },
        { time: 'D1', label: 'Module 5 — Governance Architecture: King IV' },
        { time: 'D1', label: 'Module 6 — The Enterprise ESG Data-Owner RACI Matrix' },
      ],
      [
        { time: 'D2', label: 'Module 7 — GRI-Aligned Report Structure' },
        { time: 'D2', label: 'Module 8 — Worked Example: Environmental Disclosures' },
        { time: 'D2', label: 'Module 9 — Worked Example: Social and Governance Disclosures' },
        { time: 'D2', label: 'Module 10 — JSE Guidance and Listed Companies' },
        { time: 'D2', label: 'Module 11 — Climate Risk Fundamentals' },
        { time: 'D2', label: 'Module 12 — How ESG Ratings Agencies Score Companies' },
      ],
      [
        { time: 'D3', label: 'Module 13 — Assurance and Verification Readiness' },
        { time: 'D3', label: 'Module 14 — Investor and Lender Due-Diligence Expectations' },
        { time: 'D3', label: 'Module 15 — Running a Supply-Chain ESG Audit' },
        { time: 'D3', label: "Module 16 — Capstone: Report and 12-Month Plan" },
      ],
    ],
  },

  // ---------------------------------------------------------------------
  // Module 1 — Owning ESG Implementation End to End
  // ---------------------------------------------------------------------
  {
    id: 4, layout: 'intro', module: 1,
    kicker: 'Module 01', title: 'Owning ESG Implementation End to End',
    lead: 'Define the mandate, resourcing and governance a single ESG owner needs to run implementation across a full organisation.',
    outcome: 'By the end you can draft your own implementation charter naming your mandate, sponsor, resourcing and the departments you need to coordinate.',
    templateAsset: 'team-collaboration', imageChrome: 'none',
  },
  {
    id: 5, layout: 'lead', module: 1,
    kicker: 'Module 01', title: 'The ESG owner is a translator, not a lone operator',
    lead: 'At this scale you act as a translator between the board, technical specialists and operational departments.',
    body: 'You need an executive sponsor and a documented mandate, and you must secure time commitments from finance, operations, HR and procurement before data collection begins, not after. Without this upfront work, every subsequent module in this course becomes harder to deliver.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 6, layout: 'cards', module: 1,
    kicker: 'Module 01', title: 'What each function must provide',
    lead: 'Four functions, four different kinds of evidence.',
    columns: 4,
    cards: [
      { title: 'Finance', description: 'General ledger data, capex/opex for ESG initiatives, investor relations context.' },
      { title: 'Operations', description: 'Site-level environmental data and safety records.' },
      { title: 'HR', description: 'Workforce, training and pay-equity data.' },
      { title: 'Procurement', description: 'Supplier lists, spend data and contract terms.' },
    ],
  },
  {
    id: 7, layout: 'keyterm', module: 1,
    kicker: 'Key term', term: 'Written mandate',
    definition: 'A documented mandate and named contacts in each function, secured before your first cross-departmental data request.',
    body: "Without this, collection stalls in month one. A written mandate is what turns \"ESG is everyone's job\" into departments who actually respond when you ask.",
    templateAsset: 'icon-ownership',
    callouts: [
      { label: 'Secure it early', text: 'Secure a written mandate and named contacts in each function before your first data request, or collection will stall in month one.' },
    ],
  },
  {
    id: 8, layout: 'exercise', module: 1,
    kicker: 'Exercise 1.1', meta: 'Charter drafting', title: 'Draft your three-day implementation charter',
    steps: [
      { title: 'Name your mandate and sponsor', detail: 'Who gave you this mandate, and who is your executive sponsor?' },
      { title: 'List the departments you must coordinate', detail: 'Finance, operations, HR, procurement — and any others specific to your organisation.' },
      { title: 'Note what resourcing you actually have', detail: 'Time, budget and people, honestly stated.' },
    ],
    checkFor: 'you can name a sponsor, a mandate and the departments you will need to coordinate, not just a general intention to "do ESG".',
  },

  // ---------------------------------------------------------------------
  // Module 2 — Advanced Materiality: Facilitation and Scoring
  // ---------------------------------------------------------------------
  {
    id: 9, layout: 'intro', module: 2,
    kicker: 'Module 02', title: 'Advanced Materiality — Facilitation and Scoring',
    lead: 'Facilitate a rigorous, cross-functional materiality assessment and defend the scoring methodology to sceptical stakeholders.',
    outcome: 'By the end you can design a materiality workshop and defend a documented scoring rubric to a sceptical stakeholder.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 10, layout: 'lead', module: 2,
    kicker: 'Module 02', title: 'Facilitation is a skill, not a form to fill in',
    lead: 'Include an internal cross-functional group and, where feasible, external stakeholders such as customers, investors, communities and regulators.',
    body: 'A good workshop surfaces disagreement rather than suppressing it, and separates "topics we care about" from "topics we can actually evidence" so the final list is honest about both.',
    templateAsset: 'conversation-coaching', imageChrome: 'none',
  },
  {
    id: 11, layout: 'cards', module: 2,
    kicker: 'Module 02', title: 'Facilitation techniques that surface real disagreement',
    columns: 3,
    cards: [
      { title: 'Anonymous pre-scoring', description: 'Surfaces honest views before groupthink sets in.' },
      { title: 'Structured debate on outliers', description: 'Resolves large scoring gaps with evidence, not seniority.' },
      { title: "Devil's advocate role", description: 'Stress-tests topics that look artificially low-risk.' },
    ],
    footer: 'Where internal and external stakeholders score a topic very differently, that gap is itself useful information, not a problem to average away.',
  },
  {
    id: 12, layout: 'keyterm', module: 2,
    kicker: 'Key term', term: 'Scoring rubric',
    definition: 'Documented criteria per axis: financial significance can consider revenue exposure, cost exposure, access to capital and litigation exposure; impact significance can consider scale, scope, irremediability and likelihood.',
    body: 'These concepts are drawn from recognised due-diligence thinking rather than a fixed numeric formula. The output is a prioritisation tool, not a scientific instrument, so resist false precision.',
    templateAsset: 'thinking-learner',
    callouts: [
      { label: 'Resist false precision', text: 'A materiality score is a prioritisation tool, not a scientific instrument. Do not present it as more precise than the evidence behind it.' },
    ],
  },
  {
    id: 13, layout: 'timeline', module: 2,
    kicker: 'Module 02', title: 'Path to board validation',
    lead: 'Five steps that turn a working-group view into an organisational decision.',
    templateAsset: 'problem-solving',
    steps: [
      { era: 'Step 1', title: 'Draft the matrix with rationale', description: 'Document the scoring reasoning behind every topic.' },
      { era: 'Step 2', title: 'Pressure-test with the working group', description: 'Get cross-functional challenge before it goes further.' },
      { era: 'Step 3', title: 'Present to the executive sponsor', description: 'Agree the recommended top material topics.' },
      { era: 'Step 4', title: 'Table at board or committee level', description: 'Secure formal validation, not informal agreement.' },
      { era: 'Step 5', title: 'Record the decision and review date', description: 'Note it in governance minutes for future traceability.' },
    ],
  },
  {
    id: 14, layout: 'exercise', module: 2,
    kicker: 'Exercise 2.1', meta: 'Workshop design', title: 'Design a materiality workshop agenda',
    steps: [
      { title: 'List participants', detail: 'Internal cross-functional group, plus external stakeholders where feasible.' },
      { title: 'Choose a scoring method', detail: 'Decide how financial and impact significance will each be scored.' },
      { title: 'Plan for disagreement', detail: 'Decide in advance how outlier scores and disagreements will be resolved.' },
    ],
    checkFor: 'your agenda names real participants and a real method for resolving disagreement, not just a generic list of topics.',
  },

  // ---------------------------------------------------------------------
  // Module 3 — Environmental Data Architecture at Scale
  // ---------------------------------------------------------------------
  {
    id: 15, layout: 'intro', module: 3,
    kicker: 'Module 03', title: 'Environmental Data Architecture — Emissions and Energy at Scale',
    lead: 'Design an emissions and energy data architecture that can scale across multiple sites, business units or subsidiaries.',
    outcome: 'By the end you can sketch a data-flow description for emissions and energy data that scales beyond a single spreadsheet.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 16, layout: 'lead', module: 3,
    kicker: 'Module 03', title: 'Fix the consolidation approach before you connect a second site',
    lead: 'The consolidation approach — operational control, financial control or equity share — must be fixed and applied consistently across the group.',
    body: 'Data typically flows from site-level meters and invoices to a site owner, up to a business-unit data owner, and finally to a group data owner who produces the consolidated figure. Version control and a clear audit trail matter as much as the number itself.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 17, layout: 'cards', module: 3,
    kicker: 'Module 03', title: 'Three layers, three responsible parties',
    columns: 3,
    cards: [
      { title: 'Site', description: 'Raw meter readings and invoices.', tag: 'Site facilities or operations lead' },
      { title: 'Business unit', description: 'Validated site-level data, consolidated to BU level.', tag: 'Business-unit data owner' },
      { title: 'Group', description: 'Consolidated group figures with method notes.', tag: 'Group ESG data owner' },
    ],
  },
  {
    id: 18, layout: 'keyterm', module: 3,
    kicker: 'Key term', term: 'Energy intensity ratio',
    definition: 'An intensity ratio with a denominator meaningful to the business — per unit produced, per square metre or per unit of revenue — so growth and efficiency can be told apart.',
    body: 'Track total consumption and energy mix, renewable versus non-renewable, and generation versus grid purchase, per site, and roll these up consistently.',
    templateAsset: 'icon-consistency',
    callouts: [
      { label: 'Confirm before publishing', text: 'Confirm current GHG Protocol guidance and any sector-specific emission factor sources with a qualified specialist before publishing group-level figures. Do not invent conversion factors.' },
    ],
  },
  {
    id: 19, layout: 'numbered', module: 3,
    kicker: 'Module 03', title: 'Data quality controls for water and waste',
    lead: 'Water and waste data usually starts out messier than emissions and energy data.',
    templateAsset: 'icon-professionalism',
    items: [
      { title: 'Standardise units and definitions', description: 'Fix these across every site before consolidating.' },
      { title: 'Flag estimated versus metered data', description: 'Make the distinction explicit in your records.' },
      { title: 'Set a variance threshold', description: 'A year-on-year variance beyond the threshold should trigger a query.' },
      { title: 'Retain source evidence centrally', description: 'Keep meter photos, waste manifests and invoices together.' },
      { title: 'Reconcile site to group totals', description: 'Check the roll-up before publication.' },
    ],
  },
  {
    id: 20, layout: 'exercise', module: 3,
    kicker: 'Exercise 3.1', meta: 'Data-flow sketch', title: 'Sketch your emissions and energy data flow',
    steps: [
      { title: 'Name your consolidation approach', detail: 'Operational control, financial control or equity share.' },
      { title: 'Trace the flow from site to group', detail: 'Site meters and invoices, to business-unit owner, to group owner.' },
      { title: 'Flag where the flow breaks today', detail: 'Where is version control or audit trail currently missing?' },
    ],
    checkFor: 'your sketch names a consolidation approach and shows where the flow currently breaks down.',
  },

  // ---------------------------------------------------------------------
  // Module 4 — Social Data Architecture
  // ---------------------------------------------------------------------
  {
    id: 21, layout: 'intro', module: 4,
    kicker: 'Module 04', title: 'Social Data Architecture — Workforce, Community and Human Rights',
    lead: 'Design a group-wide social data architecture covering workforce, community and supply-chain human-rights indicators.',
    outcome: 'By the end you can list the social metrics you need from each department and name who currently holds that data.',
    templateAsset: 'conversation-coaching', imageChrome: 'none',
  },
  {
    id: 22, layout: 'lead', module: 4,
    kicker: 'Module 04', title: 'Social data is the hardest data-engineering problem in the programme',
    lead: 'Workforce metrics — headcount, demographics, turnover, training, pay equity and health and safety — are often held in different HRIS systems per subsidiary.',
    body: 'Community metrics — CSI or social investment spend, local procurement and community grievance records — are often scattered across finance and community relations teams. Agree common definitions, for example what counts as a "recordable" safety incident, before you consolidate group-level figures, or the total will not mean anything.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 23, layout: 'cards', module: 4,
    kicker: 'Module 04', title: 'Where social data actually lives',
    columns: 3,
    cards: [
      { title: 'Workforce composition', description: 'Inconsistent job-level definitions across entities.', tag: 'HRIS per subsidiary' },
      { title: 'Health and safety', description: 'Different incident classification standards.', tag: 'Safety management system' },
      { title: 'Community investment', description: 'Spend not tagged consistently to ESG categories.', tag: 'Finance / CSI records' },
    ],
  },
  {
    id: 24, layout: 'keyterm', module: 4,
    kicker: 'Key term', term: 'The due-diligence cycle',
    definition: 'Identify, prevent or mitigate, track effectiveness, communicate, remediate — drawn from internationally recognised business and human rights concepts.',
    body: 'Segment suppliers by sector, geography, spend and use of labour brokers. It is also useful to distinguish whether the organisation is causing a harm directly, contributing to it, or is directly linked to it through a business relationship, since the appropriate response differs.',
    templateAsset: 'thinking-learner',
    callouts: [
      { label: 'Confirm the legal picture', text: 'Confirm any binding human-rights due diligence legal obligations that apply to your sector and the jurisdictions you operate or sell into with qualified legal advisers; expectations and legislation in this area continue to evolve.' },
    ],
  },
  {
    id: 25, layout: 'cards', module: 4,
    kicker: 'Module 04', title: 'Match due-diligence depth to risk tier',
    columns: 3,
    cards: [
      { title: 'Low risk', description: 'Standard supplier code of conduct and self-declaration.' },
      { title: 'Medium risk', description: 'Self-assessment questionnaire plus desktop verification.' },
      { title: 'High risk', description: 'On-site or third-party audit, remediation plan tracked to closure.' },
    ],
  },
  {
    id: 26, layout: 'exercise', module: 4,
    kicker: 'Exercise 4.1', meta: 'Data ownership map', title: 'Map your social data sources',
    steps: [
      { title: 'List the social metrics you need', detail: 'Workforce, community and supply-chain human-rights indicators, at group level.' },
      { title: 'Name who holds each one today', detail: 'Which system, and which department?' },
      { title: 'Flag inconsistent definitions', detail: 'Any metric where subsidiaries currently use different definitions.' },
    ],
    checkFor: 'you can name, for each metric, a current data source and whether its definition is consistent across the group.',
  },

  // ---------------------------------------------------------------------
  // Module 5 — Governance Architecture: King IV
  // ---------------------------------------------------------------------
  {
    id: 27, layout: 'intro', module: 5,
    kicker: 'Module 05', title: 'Governance Architecture — King IV and Assurance-Ready Controls',
    lead: 'Design a governance architecture, from board oversight to internal controls, that will withstand external scrutiny.',
    outcome: 'By the end you can map your governance structure against King IV areas and identify your biggest control gap.',
    templateAsset: 'action-communication', imageChrome: 'none',
  },
  {
    id: 28, layout: 'lead', module: 5,
    kicker: 'Module 05', title: '"Apply and explain" rewards substance over box-ticking',
    lead: "King IV's \"apply and explain\" basis pushes organisations toward substantive disclosure rather than box-ticking, since a vague explanation is itself visible to a careful reader.",
    body: 'Confirm the current published edition of King IV and any sector-specific supplements before mapping your governance structure against it in a formal disclosure.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 29, layout: 'cards', module: 5,
    kicker: 'Module 05', title: 'Turning principles into practice',
    columns: 4,
    cards: [
      { title: 'Ethical leadership', description: 'Documented code of conduct with a whistleblowing channel.' },
      { title: 'Strategy and risk integration', description: 'Sustainability risks on the group risk register with named owners.' },
      { title: 'Reporting integrity', description: 'A defined sign-off chain before any ESG disclosure is published.' },
      { title: 'Stakeholder relationships', description: 'A stakeholder engagement policy with a documented cadence.' },
    ],
  },
  {
    id: 30, layout: 'keyterm', module: 5,
    kicker: 'Key term', term: 'Committee structure',
    definition: 'The audit committee oversees financial and non-financial control; the Social and Ethics Committee — a statutory requirement for certain company categories under the Companies Act Regulations, subject to confirming applicability — oversees social, ethics and transformation matters; a risk committee typically sits alongside them, with an ESG steering committee reporting in at executive level.',
    body: 'External assurance providers do not just check numbers; they check whether the control environment behind those numbers is real.',
    templateAsset: 'team-collaboration',
    callouts: [
      { label: 'Confirm applicability', text: 'Confirm whether the Social and Ethics Committee requirement applies to your specific company category before assuming it does or does not.' },
    ],
  },
  {
    id: 31, layout: 'numbered', module: 5,
    kicker: 'Module 05', title: 'Building an assurance-ready control environment',
    items: [
      { title: 'Document a sign-off chain', description: 'From data owner through to board, for every disclosed metric.' },
      { title: 'Retain source evidence and calculation records', description: 'Keep these for every disclosed figure, not just the total.' },
      { title: 'Log restatements with reasons', description: 'A clear reason for every prior-period change.' },
      { title: 'Test controls internally first', description: 'Find gaps before an external assurance provider does.' },
      { title: 'Keep substantive committee minutes', description: 'Minutes should show real oversight, not a standing agenda item.' },
    ],
  },
  {
    id: 32, layout: 'exercise', module: 5,
    kicker: 'Exercise 5.1', meta: 'Gap mapping', title: 'Map your governance against King IV',
    steps: [
      { title: 'List your current committees', detail: 'Audit, risk, social and ethics, ESG steering — whichever exist today.' },
      { title: 'Map each King IV area', detail: 'Ethical leadership, strategy and risk, reporting integrity, stakeholder relationships.' },
      { title: 'Name your biggest control gap', detail: 'Where would an assurance provider find the least real oversight?' },
    ],
    checkFor: 'you can name one specific control gap, not just a general sense that governance "could be stronger".',
  },

  // ---------------------------------------------------------------------
  // Module 6 — The Enterprise ESG Data-Owner RACI Matrix
  // ---------------------------------------------------------------------
  {
    id: 33, layout: 'intro', module: 6,
    kicker: 'Module 06', title: 'Building the Enterprise ESG Data-Owner RACI Matrix',
    lead: 'Build a full RACI matrix assigning responsibility, accountability, consultation and information roles for ESG data across finance, operations, HR and procurement.',
    outcome: 'By the end you can complete a RACI matrix for five ESG metrics spanning at least three core departments.',
    templateAsset: 'icon-ownership', imageChrome: 'none',
  },
  {
    id: 34, layout: 'keyterm', module: 6,
    kicker: 'Key term', term: 'RACI',
    definition: 'Responsible does the work of collecting the data. Accountable answers for its accuracy, and should be exactly one person per metric. Consulted gives input before a method changes. Informed is told once the outcome is finalised.',
    body: 'Applying this to ESG data means naming, for every metric, who holds each of these four roles.',
    callouts: [
      { label: 'Never split accountability', text: 'Never assign two people as Accountable for the same metric. Shared accountability quietly becomes no accountability.' },
    ],
  },
  {
    id: 35, layout: 'cards', module: 6,
    kicker: 'Module 06', title: 'A worked RACI example across departments',
    columns: 4,
    cards: [
      { title: 'Energy consumption', description: 'Operations: Responsible & Accountable. Finance & HR: Informed. Procurement: Consulted on utility contracts.' },
      { title: 'Workforce turnover', description: 'HR: Responsible & Accountable. Finance, Operations & Procurement: Informed.' },
      { title: 'Supplier human-rights screening', description: 'Procurement: Responsible & Accountable. Operations & HR: Consulted. Finance: Informed.' },
      { title: 'Community investment spend', description: 'Finance: Responsible & Accountable. HR: Consulted. Operations & Procurement: Informed.' },
    ],
  },
  {
    id: 36, layout: 'numbered', module: 6,
    kicker: 'Module 06', title: 'Building your RACI matrix',
    items: [
      { title: 'List every material metric', description: 'Draw the list directly from your materiality assessment.' },
      { title: 'Name one Accountable owner', description: 'No more than one person accountable per metric.' },
      { title: 'Assign Responsible parties', description: 'The people who actually hold the source data.' },
      { title: 'List who must be Consulted', description: 'Before the method for a metric changes.' },
      { title: 'Confirm who is Informed', description: 'Once figures are finalised each cycle.' },
      { title: 'Review annually or after reorganisation', description: 'Reconfirm every owner still holds the role they were assigned.' },
    ],
  },
  {
    id: 37, layout: 'exercise', module: 6,
    kicker: 'Exercise 6.1', meta: 'RACI drafting', title: 'Complete a RACI matrix for five metrics',
    steps: [
      { title: 'Pick five ESG metrics', detail: 'Spanning at least three of finance, operations, HR and procurement.' },
      { title: 'Assign R, A, C and I for each', detail: 'Exactly one Accountable person per metric.' },
      { title: 'Check for shared accountability', detail: 'Fix any metric with more than one person marked Accountable.' },
    ],
    checkFor: 'every metric has exactly one Accountable owner and at least one Responsible party named.',
  },

  // ---------------------------------------------------------------------
  // Module 7 — GRI-Aligned Report Structure and General Disclosures
  // ---------------------------------------------------------------------
  {
    id: 38, layout: 'intro', module: 7,
    kicker: 'Module 07', title: 'GRI-Aligned Report Structure and General Disclosures',
    lead: 'Structure a full GRI-aligned sustainability report and complete its general disclosures section with real organisational information.',
    outcome: 'By the end you can draft the general disclosures section of your own report.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 39, layout: 'lead', module: 7,
    kicker: 'Module 07', title: "General disclosures earn the reader's trust first",
    lead: 'They tell the reader who you are, how you are governed and how you engaged stakeholders before you show them a single metric.',
    body: "GRI's Universal Standards concept covers organisational profile, activities and workers, governance, strategy, policies and practices, and stakeholder engagement and reporting practice. Confirm the current published version of the GRI Standards and specific disclosure numbering before finalising your index.",
    templateAsset: 'action-communication', imageChrome: 'none',
  },
  {
    id: 40, layout: 'cards', module: 7,
    kicker: 'Module 07', title: 'The general disclosures checklist',
    columns: 5,
    cards: [
      { title: 'Organisational profile', description: 'Legal name, ownership structure, markets served, scale of operations.' },
      { title: 'Governance', description: 'Governance structure, committees, delegation of authority for sustainability.' },
      { title: 'Strategy', description: 'Statement from the most senior decision-maker, key impacts, risks and opportunities.' },
      { title: 'Stakeholder engagement', description: 'Approach, groups engaged, key topics raised.' },
      { title: 'Reporting practice', description: 'Boundary, entities included, period, restatements, contact point.' },
    ],
  },
  {
    id: 41, layout: 'keyterm', module: 7,
    kicker: 'Key term', term: 'Leadership statement',
    definition: "The statement from the organisation's most senior decision-maker, part of the strategy area of general disclosures.",
    body: 'Write it last, once every other section is drafted, so it accurately reflects what the report actually says.',
    callouts: [
      { label: 'Why last', text: 'A leadership statement written before the rest of the report risks describing a report that does not yet exist.' },
    ],
  },
  {
    id: 42, layout: 'exercise', module: 7,
    kicker: 'Exercise 7.1', meta: 'Drafting', title: 'Draft your general disclosures section',
    steps: [
      { title: 'Complete the checklist areas', detail: 'Organisational profile, governance, strategy, stakeholder engagement, reporting practice.' },
      { title: "Use your own organisation's details", detail: 'Structure, ownership and governance details as they actually stand.' },
      { title: 'Leave the leadership statement for last', detail: 'Draft everything else first.' },
    ],
    checkFor: "every checklist area is complete and consistent with your organisation's actual governance records.",
  },

  // ---------------------------------------------------------------------
  // Module 8 — Worked Example: Environmental Disclosures
  // ---------------------------------------------------------------------
  {
    id: 43, layout: 'intro', module: 8,
    kicker: 'Module 08', title: 'Worked Example — Drafting the Environmental Disclosures Section',
    lead: 'Draft a complete environmental disclosures section for a material topic using a worked example.',
    outcome: "By the end you can draft your own organisation's energy or emissions disclosure in full.",
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 44, layout: 'lead', module: 8,
    kicker: 'Module 08', title: 'State the boundary, the method, the number, and what changed — in that order',
    lead: 'A fictional mid-sized manufacturer, "Company X", discloses its energy consumption by stating the reporting boundary, the method, the current and prior period figures, and a brief plain-language explanation of the year-on-year change.',
    body: 'This lesson does not invent specific numeric values; the structure is what matters, not any placeholder figure.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 45, layout: 'timeline', module: 8,
    kicker: 'Module 08', title: 'The five-step disclosure structure',
    templateAsset: 'action-communication',
    steps: [
      { era: 'Step 1', title: 'State the boundary and method', description: 'Name the consolidation approach used.' },
      { era: 'Step 2', title: 'Present current and prior period figures', description: 'Side by side, so the reader can compare directly.' },
      { era: 'Step 3', title: 'Explain material movement', description: 'In plain language, not jargon.' },
      { era: 'Step 4', title: 'Note any restatement', description: 'Explain why a prior figure changed, if it did.' },
      { era: 'Step 5', title: 'Cross-reference the management approach', description: 'Link to the relevant target, if one exists.' },
    ],
  },
  {
    id: 46, layout: 'worked-example', module: 8,
    kicker: 'Module 08 · Worked example', title: 'Same disclosure, two ways to write it',
    weak: { label: 'Weak', prompt: '"We consumed energy across our operations this year, in line with our ongoing sustainability commitment."', result: 'No stated boundary, no method, no comparable prior period and no explanation of change. Nothing here could be traced or assured.' },
    strong: { label: 'Strong', prompt: 'States the boundary (all owned and leased manufacturing sites), the method (utility invoices reconciled to meter readings, consolidated on an operational control basis), current and prior period figures side by side, and a plain-language explanation of what changed.', result: 'Every element can be traced to evidence and compared year to year — this is how "Company X"\'s worked example in this module is built.' },
    footnote: 'Reusing this exact five-step structure for every environmental topic disclosure makes the whole report far easier for a reader, and an assurance provider, to follow.',
  },
  {
    id: 47, layout: 'exercise', module: 8,
    kicker: 'Exercise 8.1', meta: 'Worked drafting', title: 'Draft your own energy or emissions disclosure',
    steps: [
      { title: 'State your boundary and method', detail: 'Name the consolidation approach you actually use.' },
      { title: 'Present current and prior figures', detail: 'Side by side, using your own real or placeholder data.' },
      { title: 'Explain the movement', detail: 'In plain language, and note any restatement.' },
    ],
    checkFor: 'your draft follows all five steps in order, with no invented figures standing in for real data.',
  },

  // ---------------------------------------------------------------------
  // Module 9 — Worked Example: Social and Governance Disclosures
  // ---------------------------------------------------------------------
  {
    id: 48, layout: 'intro', module: 9,
    kicker: 'Module 09', title: 'Worked Example — Drafting the Social and Governance Disclosures Sections',
    lead: 'Draft complete social and governance disclosures for material topics using worked examples.',
    outcome: "By the end you can draft your own organisation's workforce and governance disclosures in full.",
    templateAsset: 'conversation-coaching', imageChrome: 'none',
  },
  {
    id: 49, layout: 'lead', module: 9,
    kicker: 'Module 09', title: 'The same evidence discipline, more narrative',
    lead: 'Social and governance disclosures carry more narrative and less pure metric than environmental ones, which makes it easier to drift into vague language.',
    body: 'They still need the same evidence discipline: a stated scope, a stated method, and a real, checkable record behind every claim.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 50, layout: 'cards', module: 9,
    kicker: 'Module 09', title: 'Two worked examples',
    columns: 2,
    cards: [
      { title: 'Workforce training', description: 'States which employees are covered (all permanent staff), the method (training hours logged in the learning management system), current and prior period figures, and what drove any change.' },
      { title: 'Board oversight', description: 'Names the committee responsible, states how often it meets on the topic, and references the King IV principle it maps to.' },
    ],
    footer: "Confirm your own committee's actual mandate and meeting record before publishing this kind of disclosure; never describe oversight that does not exist.",
  },
  {
    id: 51, layout: 'keyterm', module: 9,
    kicker: 'Key term', term: 'Governance-related greenwashing',
    definition: 'Describing a governance structure or committee mandate in a disclosure that does not actually exist or is not yet formally documented.',
    body: 'This is one of the most common sources of governance-related greenwashing, and one of the easiest to avoid: only describe oversight you can point to in real minutes.',
    callouts: [
      { label: 'The rule', text: 'Never describe oversight that does not exist.' },
    ],
  },
  {
    id: 52, layout: 'exercise', module: 9,
    kicker: 'Exercise 9.1', meta: 'Worked drafting', title: 'Draft your workforce and governance disclosures',
    steps: [
      { title: 'Draft the workforce training disclosure', detail: 'Coverage, method, current and prior figures, and what drove the change.' },
      { title: 'Draft the board oversight disclosure', detail: 'Committee name, meeting cadence, and the King IV principle it maps to.' },
      { title: 'Verify against real records', detail: 'Confirm every claim against actual minutes and logs before finalising.' },
    ],
    checkFor: 'neither disclosure describes a committee, mandate or record that does not actually exist yet.',
  },

  // ---------------------------------------------------------------------
  // Module 10 — JSE Guidance and Listed-Company Considerations
  // ---------------------------------------------------------------------
  {
    id: 53, layout: 'intro', module: 10,
    kicker: 'Module 10', title: 'JSE Guidance and Listed-Company Considerations',
    lead: "Apply JSE Sustainability Disclosure Guidance concepts to a listed or large private company's reporting decisions.",
    outcome: 'By the end you can identify which sections of your report could be cross-referenced to JSE guidance categories, and where a gap remains.',
    templateAsset: 'action-communication', imageChrome: 'none',
  },
  {
    id: 54, layout: 'lead', module: 10,
    kicker: 'Module 10', title: 'Relevant if you are listed, supply listed companies, or plan to list',
    lead: 'If your organisation is listed, supplies listed companies, or is preparing for future listing, JSE sustainability guidance becomes directly relevant to how you structure disclosure.',
    body: 'The guidance generally aims to help issuers give investors decision-useful sustainability information, often encouraging alignment with recognised international frameworks and climate-related disclosure concepts.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 55, layout: 'keyterm', module: 10,
    kicker: 'Key term', term: 'JSE Sustainability Disclosure Guidance',
    definition: 'Guidance that generally aims to help issuers give investors decision-useful sustainability information, often aligned to recognised frameworks.',
    body: 'The JSE periodically updates this guidance; confirm the current published version and any listing requirement cross-references directly from the JSE before making a compliance decision.',
    callouts: [
      { label: 'Do not rely on this summary', text: "Do not present this course's summary as a substitute for reading the current published JSE guidance yourself or consulting your sponsor or legal adviser." },
    ],
  },
  {
    id: 56, layout: 'cards', module: 10,
    kicker: 'Module 10', title: 'A practical cross-referencing approach',
    columns: 3,
    cards: [
      { title: 'Climate governance narrative', description: 'Links to climate-related disclosure expectations.' },
      { title: 'Standardised metrics', description: 'Links to recognised international metric sets.' },
      { title: 'Board oversight disclosure', description: 'Links to King IV.' },
    ],
  },
  {
    id: 57, layout: 'exercise', module: 10,
    kicker: 'Exercise 10.1', meta: 'Cross-referencing', title: 'Map your report against JSE guidance categories',
    steps: [
      { title: 'List your report elements', detail: 'Climate narrative, standardised metrics, board oversight disclosure.' },
      { title: 'Cross-reference each to a JSE guidance category', detail: 'Where a plausible link exists.' },
      { title: 'Flag any gap', detail: 'Anything not yet cross-referenced goes on a list for legal or sponsor review.' },
    ],
    checkFor: 'you can name at least one gap that still needs a legal adviser or your sponsor to confirm.',
  },

  // ---------------------------------------------------------------------
  // Module 11 — Climate Risk Fundamentals
  // ---------------------------------------------------------------------
  {
    id: 58, layout: 'intro', module: 11,
    kicker: 'Module 11', title: 'Climate Risk Fundamentals — Physical Risk, Transition Risk and Scenarios',
    lead: 'Distinguish physical and transition climate risk conceptually and describe how scenario thinking is used in climate-risk assessment.',
    outcome: "By the end you can list plausible physical and transition climate risks for your own operations or value chain.",
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 59, layout: 'lead', module: 11,
    kicker: 'Module 11', title: 'Two categories, assessed together',
    lead: 'Climate-related financial disclosure frameworks, most influentially the work associated with the TCFD, popularised a simple but powerful split: physical risk and transition risk.',
    body: 'This course describes these categories conceptually without inventing specific probability or cost figures, which should come from recognised climate data and specialist providers.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 60, layout: 'comparison', module: 11,
    kicker: 'Module 11', title: 'Physical risk and transition risk',
    columns: [
      { heading: 'Physical risk', items: ['Acute — extreme weather events, flooding or wildfire, immediate to short-term.', 'Chronic — gradual shifts in average temperatures, changing rainfall patterns or sea-level rise, long-term.'] },
      { heading: 'Transition risk', items: ['Policy and legal — new carbon pricing or stricter emissions regulation.', 'Technology — existing assets becoming obsolete.', 'Market — shifting customer or investor demand.', 'Reputational risk.'] },
    ],
    callout: { label: 'A useful exercise', text: 'For every material asset or site, ask what happens to it if the climate changes, and separately, what happens to it if the economy decarbonises.' },
  },
  {
    id: 61, layout: 'keyterm', module: 11,
    kicker: 'Key term', term: 'Climate scenario',
    definition: 'An internally consistent, plausible description of how the future could unfold, used to test resilience rather than predict a single outcome.',
    body: 'TCFD-inspired work typically contrasts a lower-warming, faster-transition pathway against a higher-warming, slower-transition pathway, without asserting specific temperature or cost figures, which should come from recognised scenario providers.',
    templateAsset: 'icon-consistency',
    callouts: [
      { label: 'Know the limits', text: 'Do not present a simple internal scenario exercise as equivalent to a full TCFD-aligned climate risk assessment. Confirm current TCFD-related guidance and consider specialist input for a formal assessment.' },
    ],
  },
  {
    id: 62, layout: 'numbered', module: 11,
    kicker: 'Module 11', title: 'A simple scenario-thinking exercise',
    items: [
      { title: 'Pick two contrasting pathways', description: 'Choose pathways relevant to your sector.' },
      { title: 'Ask what changes for assets, costs and revenue', description: 'Under each pathway separately.' },
      { title: 'Identify resilient decisions', description: 'Ones that look sound across both pathways.' },
      { title: 'Flag single-pathway decisions', description: 'Mark decisions that only work in one pathway as higher risk.' },
      { title: 'Document it as a starting point', description: 'Not a finished climate risk assessment.' },
    ],
  },
  {
    id: 63, layout: 'exercise', module: 11,
    kicker: 'Exercise 11.1', meta: 'Risk listing', title: 'List your climate risks',
    steps: [
      { title: 'List two physical risks', detail: 'Plausible for your own operations or value chain.' },
      { title: 'List two transition risks', detail: 'Plausible for your own sector and markets.' },
      { title: 'Ask the two-pathway question', detail: 'For one material asset or site, what changes under each pathway?' },
    ],
    checkFor: 'each risk is specific to your own operations or value chain, not a generic industry statement.',
  },

  // ---------------------------------------------------------------------
  // Module 12 — How ESG Ratings Agencies Score Companies
  // ---------------------------------------------------------------------
  {
    id: 64, layout: 'intro', module: 12,
    kicker: 'Module 12', title: 'How ESG Ratings Agencies Score Companies',
    lead: 'Describe, in general terms, how ESG ratings agencies approach scoring so you can anticipate and respond to a ratings assessment.',
    outcome: "By the end you can list which of your current disclosures would likely be visible and usable to an external ratings agency today.",
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 65, layout: 'lead', module: 12,
    kicker: 'Module 12', title: 'Ratings agencies score what is disclosed and verifiable',
    lead: 'ESG ratings agencies generally score what is publicly disclosed and verifiable, not what an organisation privately believes about itself.',
    body: 'Providers such as MSCI and Sustainalytics-style methodologies are described here only in general terms, not with their exact proprietary weightings, which are provider-specific and change over time.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 66, layout: 'cards', module: 12,
    kicker: 'Module 12', title: 'What ratings providers generally look at',
    columns: 4,
    cards: [
      { title: 'Public disclosure', description: 'Sustainability report, annual report, policies.' },
      { title: 'Controversy monitoring', description: 'Media reports, litigation, regulatory action.' },
      { title: 'Industry-specific risk exposure', description: 'Sector-specific material topics.' },
      { title: 'Direct engagement', description: 'Company responses to provider questionnaires, where offered.' },
    ],
    footer: 'Ratings tend to reward transparency and verifiable data even where performance itself is still maturing, and penalise unresolved controversies more heavily than they reward marketing claims.',
  },
  {
    id: 67, layout: 'keyterm', module: 12,
    kicker: 'Key term', term: 'Ratings methodology',
    definition: 'Broadly, a combination of industry-specific material risk identification, a review of public disclosure, controversy or incident monitoring, and sometimes direct engagement with the company.',
    body: 'Each provider publishes its own methodology and proprietary weightings, which change over time.',
    templateAsset: 'icon-consistency',
    callouts: [
      { label: 'Never dispute a score from a general description', text: 'Do not rely on this general description to predict or dispute a specific rating. Confirm the specific agency\'s own published methodology directly with them before responding to a rating.' },
    ],
  },
  {
    id: 68, layout: 'exercise', module: 12,
    kicker: 'Exercise 12.1', meta: 'Visibility audit', title: 'Audit what a ratings agency would see today',
    steps: [
      { title: 'List your current public disclosures', detail: 'Sustainability report, annual report, published policies.' },
      { title: 'List any known controversies', detail: 'Media reports, litigation or regulatory action, resolved or not.' },
      { title: 'Note what is not yet publicly visible', detail: 'Anything true about your organisation that a ratings agency could not currently see.' },
    ],
    checkFor: 'you can name at least one thing your organisation does well that is not yet publicly disclosed, and could be.',
  },

  // ---------------------------------------------------------------------
  // Module 13 — Assurance and Verification Readiness
  // ---------------------------------------------------------------------
  {
    id: 69, layout: 'intro', module: 13,
    kicker: 'Module 13', title: 'Assurance and Verification Readiness',
    lead: 'Prepare an ESG report and its underlying data for external assurance or verification.',
    outcome: 'By the end you can trace one disclosed metric from the report back to source evidence.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 70, layout: 'keyterm', module: 13,
    kicker: 'Key term', term: 'Limited vs reasonable assurance',
    definition: 'Limited assurance involves a more moderate level of scrutiny, typically expressed as "nothing has come to our attention." Reasonable assurance involves a higher level of scrutiny, closer to a financial audit.',
    body: 'Confirm current assurance standards, such as those referenced by recognised assurance bodies, and the specific scope your provider will apply before an engagement.',
    templateAsset: 'thinking-learner',
    callouts: [
      { label: 'Prepare early', text: 'Whether or not your organisation is required to seek external assurance today, preparing as if you will need it produces a stronger report.' },
    ],
  },
  {
    id: 71, layout: 'cards', module: 13,
    kicker: 'Module 13', title: 'What gets tested in an assurance engagement',
    columns: 5,
    cards: [
      { title: 'Boundary', description: 'Whether the entities and sites included are consistent with the stated boundary.' },
      { title: 'Method', description: 'Whether the calculation method is documented and consistently applied.' },
      { title: 'Evidence', description: 'Whether the figure can be traced to source documents.' },
      { title: 'Controls', description: 'Whether there is a named owner and a review or sign-off step.' },
      { title: 'Narrative consistency', description: 'Whether the report text matches what the data actually shows.' },
    ],
  },
  {
    id: 72, layout: 'numbered', module: 13,
    kicker: 'Module 13', title: 'A practical assurance-readiness self-check',
    items: [
      { title: 'Pick five material metrics at random', description: 'Do not choose the ones you already know are solid.' },
      { title: 'Trace each back to source evidence', description: 'Confirm the figure is fully traceable.' },
      { title: 'Confirm sign-off happened', description: 'A named owner should have reviewed and signed off each figure.' },
      { title: 'Check the method is documented', description: 'Not just remembered by one person.' },
      { title: 'Fix any gap found now', description: 'Before, not during, the real assurance engagement.' },
    ],
  },
  {
    id: 73, layout: 'exercise', module: 13,
    kicker: 'Exercise 13.1', meta: 'Mock walk-through', title: 'Run a mock assurance walk-through',
    steps: [
      { title: 'Pick one disclosed metric', detail: 'Something you would genuinely include in a report.' },
      { title: 'Trace it back to source evidence', detail: 'From the report, to the calculation, to the underlying document.' },
      { title: 'Note where the trail breaks', detail: 'Any point where evidence, sign-off or method documentation is missing.' },
    ],
    checkFor: 'you can trace your chosen metric to source evidence, or you have honestly identified exactly where the trail breaks.',
  },

  // ---------------------------------------------------------------------
  // Module 14 — Investor and Lender ESG Due-Diligence Expectations
  // ---------------------------------------------------------------------
  {
    id: 74, layout: 'intro', module: 14,
    kicker: 'Module 14', title: 'Investor and Lender ESG Due-Diligence Expectations',
    lead: 'Describe what investors and lenders typically look for in ESG due diligence and prepare a credible response pack.',
    outcome: 'By the end you can draft a one-page ESG due-diligence summary of the kind an investor or lender would typically request.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 75, layout: 'lead', module: 14,
    kicker: 'Module 14', title: 'Investors test risk-management maturity, not just metrics',
    lead: 'ESG due diligence from an investor or lender tends to focus on risk management maturity, not just on how favourable your metrics look.',
    body: 'Common focus areas include governance and oversight of ESG risk, the materiality assessment process, data quality and assurance status, incident and controversy history, climate-risk awareness and supply-chain due-diligence maturity. Lenders financing specific projects may also apply sector-specific environmental and social risk frameworks referenced in their own policies; confirm the specific framework a lender uses directly with them.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 76, layout: 'cards', module: 14,
    kicker: 'Module 14', title: 'What investors and lenders commonly request',
    columns: 5,
    cards: [
      { title: 'Governance evidence', description: 'Board minutes referencing ESG oversight.' },
      { title: 'Policy documents', description: 'Environmental, health and safety, human-rights policies.' },
      { title: 'Data and metrics', description: 'Historical ESG metrics with method notes.' },
      { title: 'Incident history', description: 'Material environmental or social incidents and remediation.' },
      { title: 'Forward plan', description: 'Roadmap or targets for the next reporting period.' },
    ],
  },
  {
    id: 77, layout: 'keyterm', module: 14,
    kicker: 'Key term', term: 'Honest gaps',
    definition: 'A due-diligence pack that is well organised and honest about gaps, with a credible plan to close them.',
    body: 'This generally lands better with investors and lenders than a pack that only shows favourable information.',
    callouts: [
      { label: 'Resist the instinct to only show good news', text: 'Honesty about gaps, backed by a credible plan, builds more credibility than a pack of only favourable data.' },
    ],
  },
  {
    id: 78, layout: 'exercise', module: 14,
    kicker: 'Exercise 14.1', meta: 'Due-diligence pack', title: 'Draft a one-page ESG due-diligence summary',
    steps: [
      { title: 'Summarise governance and risk maturity', detail: 'What oversight and processes genuinely exist today?' },
      { title: 'List your key metrics and their status', detail: 'Include assurance status honestly, even where it is not yet in place.' },
      { title: 'Name your forward plan', detail: 'What will you improve in the next reporting period, and by when?' },
    ],
    checkFor: 'your one-pager is honest about at least one gap, alongside a credible plan to close it.',
  },

  // ---------------------------------------------------------------------
  // Module 15 — Running a Supply-Chain ESG Audit
  // ---------------------------------------------------------------------
  {
    id: 79, layout: 'intro', module: 15,
    kicker: 'Module 15', title: 'Running a Supply-Chain ESG Audit',
    lead: 'Plan and run a supply-chain ESG audit covering environmental, social and governance risk across key suppliers.',
    outcome: 'By the end you can design a supply-chain ESG audit plan for your top suppliers by spend.',
    templateAsset: 'take-action', imageChrome: 'none',
  },
  {
    id: 80, layout: 'lead', module: 15,
    kicker: 'Module 15', title: 'The largest ESG risk often sits with suppliers, not owned operations',
    lead: 'For most organisations, the largest share of ESG risk, especially social and environmental risk, sits with suppliers rather than with owned operations.',
    body: 'Decide which suppliers to include based on spend, sector risk and geography; which topics to audit, such as labour practices, health and safety, environmental compliance, anti-bribery and ethics, and sub-tier supplier management; and which method suits each risk tier.',
    templateAsset: 'problem-solving', imageChrome: 'none',
  },
  {
    id: 81, layout: 'cards', module: 15,
    kicker: 'Module 15', title: 'Match audit method and frequency to risk tier',
    columns: 3,
    cards: [
      { title: 'Low-risk / low-spend', description: 'Self-assessment questionnaire only, every 2-3 years.' },
      { title: 'Medium-risk', description: 'Desktop document review plus questionnaire, annually.' },
      { title: 'High-risk', description: 'On-site audit by trained auditor or third party, annually or more frequent, with follow-up.' },
    ],
  },
  {
    id: 82, layout: 'numbered', module: 15,
    kicker: 'Module 15', title: 'Running the audit end to end',
    items: [
      { title: 'Segment suppliers and confirm scope', description: 'Agree the scope with procurement before starting.' },
      { title: 'Notify suppliers in advance', description: 'Request required documents ahead of the review.' },
      { title: 'Conduct the review or visit', description: 'Use a fixed checklist for consistency across suppliers.' },
      { title: 'Score findings and agree a corrective plan', description: 'Agree the plan directly with the supplier.' },
      { title: 'Track corrective actions to closure', description: 'Re-audit high-risk findings until closed.' },
    ],
  },
  {
    id: 83, layout: 'keyterm', module: 15,
    kicker: 'Key term', term: 'Tracked corrective action',
    definition: 'A finding without a tracked corrective action plan is just a piece of paper.',
    body: 'Build the tracking step into the audit process from the start, not as an afterthought.',
    callouts: [
      { label: 'The point of the audit', text: 'An audit finding only becomes useful once it is tracked to closure.' },
    ],
  },
  {
    id: 84, layout: 'exercise', module: 15,
    kicker: 'Exercise 15.1', meta: 'Audit plan', title: 'Design your supply-chain audit plan',
    steps: [
      { title: 'Name your top ten suppliers by spend', detail: 'Or as close to this as your data currently allows.' },
      { title: 'Assign a risk tier to each', detail: 'Based on spend, sector risk and geography.' },
      { title: 'Choose a method and frequency per tier', detail: 'And name who will track findings to closure.' },
    ],
    checkFor: 'every supplier on your list has a risk tier, a method, and a named owner for tracking findings to closure.',
  },

  // ---------------------------------------------------------------------
  // Module 16 — Capstone: Report and 12-Month Plan
  // ---------------------------------------------------------------------
  {
    id: 85, layout: 'intro', module: 16,
    kicker: 'Module 16', title: "Capstone — Building Your Organisation's First Full ESG Report and 12-Month Improvement Plan",
    lead: "Assemble everything covered across the three days into your organisation's first full ESG report structure and a realistic 12-month improvement plan.",
    outcome: 'By the end you can produce a full draft table of contents and a quarter-by-quarter 12-month improvement plan.',
    templateAsset: 'icon-professionalism', imageChrome: 'none',
  },
  {
    id: 86, layout: 'cards', module: 16,
    kicker: 'Module 16', title: 'The final assembly checklist',
    columns: 5,
    cards: [
      { title: 'Materiality matrix', description: 'Board-validated and dated.' },
      { title: 'General disclosures', description: 'Complete and consistent with governance records.' },
      { title: 'Topic disclosures', description: 'One per material topic, following the five-step structure.' },
      { title: 'Data controls', description: 'Passed the assurance-readiness self-check.' },
      { title: 'Claims review', description: 'Every claim passed the greenwashing check.' },
    ],
  },
  {
    id: 87, layout: 'keyterm', module: 16,
    kicker: 'Key term', term: 'One final editor',
    definition: 'Assign one final editor to read the whole report end to end for consistency before publication.',
    body: 'Multiple authors nearly always leave inconsistencies that a single final read will catch.',
    callouts: [
      { label: 'Before publication', text: 'Confirm the materiality matrix is board-validated and dated, every material topic has an assigned RACI owner and a drafted disclosure, general disclosures and the leadership statement are complete and consistent with governance records, data has passed the assurance-readiness self-check, and every claim has passed the greenwashing check.' },
    ],
  },
  {
    id: 88, layout: 'lead', module: 16,
    kicker: 'Module 16', title: 'Sequence the plan by dependency, not by ease',
    lead: 'Close governance and data-ownership gaps first, since everything else depends on them, then improve data quality and coverage, then strengthen target-setting, then build toward assurance readiness.',
    body: 'This mirrors the dependency logic used throughout this course. The improvement plan is what turns this course from a one-time exercise into an ongoing, credible ESG programme.',
    templateAsset: 'take-action', imageChrome: 'none',
  },
  {
    id: 89, layout: 'numbered', module: 16,
    kicker: 'Module 16', title: 'Building the 12-month improvement plan',
    items: [
      { title: 'List every gap identified', description: 'From report assembly and the assurance self-check.' },
      { title: 'Prioritise by dependency', description: 'Governance and data gaps first, not whichever is easiest.' },
      { title: 'Assign an owner and a quarter', description: 'To every improvement item on the list.' },
      { title: 'Set a measurable definition of "done"', description: 'For each item, so progress can be checked later.' },
      { title: 'Present alongside the report', description: 'Get sponsor and board sign-off on the plan together with the report.' },
    ],
  },
  {
    id: 90, layout: 'exercise', module: 16,
    kicker: 'Exercise 16.1', meta: 'Capstone', title: 'Produce your table of contents and 12-month plan',
    steps: [
      { title: 'Draft a full table of contents', detail: "Using your own organisation's structure, ownership and governance details." },
      { title: 'Build a quarter-by-quarter improvement plan', detail: 'Every gap from this course, prioritised by dependency, with an owner per quarter.' },
      { title: 'Name what you would present first to your sponsor', detail: 'The report draft, the plan, or both together?' },
    ],
    checkFor: 'your table of contents is complete and your 12-month plan sequences governance and data gaps before targets and assurance.',
  },

  // ---------------------------------------------------------------------
  // Final outcome
  // ---------------------------------------------------------------------
  {
    id: 91, layout: 'outcome',
    kicker: 'Course outcome', title: 'What you can do now',
    checks: [
      'Run a defensible, board-validated materiality assessment and build a RACI matrix that names exactly one accountable owner per metric',
      'Draft a full GRI-aligned report — general disclosures plus environmental, social and governance topic disclosures — using the five-step structure',
      'Explain physical and transition climate risk, describe how ESG ratings agencies approach scoring, and prepare a report for external assurance',
      'Assemble a due-diligence-ready evidence pack, run a supply-chain ESG audit, and sequence a 12-month improvement plan by dependency, not by ease',
    ],
    footnote: 'Confirm every regulatory reference, standard, framework version and figure in this course against the current gazetted or published source before you rely on it in a real disclosure.',
  },
];
